import { Avatar, Box, CircularProgress, Divider, SelectChangeEvent } from '@mui/material';
import CustomAvatar from '@src/components/CustomAvatar';
import LabeledSelectField from '@src/components/CustomSelect/LabeledSelectField';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import PermissionContainer, { Group } from './Components/PermissionContainer';
import CustomButton from '@src/components/CustomButton/CustomButton';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import { useAppSelector } from '@src/store/hooks';
import { CustomAlertType } from '@src/interfaces/type';
import { arraysEqual, deepEqual } from '@src/helpers/Helpers';
import { TeamView } from '.';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	max-width: 100%;
	height: 100%;
	padding: 24px;
	overflow: scroll;
	gap: 24px;
	@media screen and (max-width: 600px) {
		height: calc(100vh - 166px);
	}
	.container1 {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding-bottom: 16px;
		#heading {
			color: var(--others-black, #000);
			font-size: 20px;
			font-weight: 700;
		}
		@media screen and (max-width: 600px) {
			flex-direction: column;
			gap: 20px;
			align-items: flex-start;
		}
	}
	.buttonContainer {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding-bottom: 16px;
		#heading {
			color: var(--others-black, #000);
			font-size: 20px;
			font-weight: 700;
		}
	}
	#profile {
		display: flex;
		align-items: center;
		gap: 12px;
		#profile-info {
			display: flex;
			flex-direction: column;
			gap: 2px;
			h3 {
				color: var(--default-text-color);
				font-size: 20px;
				font-weight: 700;
			}
			p {
				color: var(--color-font-grey);
				font-size: 16px;
				font-weight: 600;
			}
		}
	}
	#access-user {
		display: flex;
		align-items: center;
		gap: 12px;
		p {
			color: var(--color-font-grey);
			font-size: 14px;
			font-weight: 600;
			line-height: 15.6px;
		}
	}
`;
interface Props {
	user: any;
	displayCustomError: any;
	setAlert: (data: CustomAlertType) => void;
	setView: (data: TeamView) => void;
}

const Permission: React.FC<Props> = ({ user, displayCustomError, setAlert, setView }) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const [accessLevel, setAccessLevel] = useState(user?.type?.toLowerCase() === 'user' ? '1' : '2');
	const [loadingData, setLoadingData] = useState(true);
	const [disable, setDisable] = useState(false);
	const [allPermissions, setAllPermissions] = useState<Group[]>([]);
	const [initialData, setInitialData] = useState<Group[]>([]);
	const [isApiCall, setApiCall] = useState(false);

	const handleChange = (event: SelectChangeEvent<unknown>) => {
		const selectedValue = event.target.value as string;
		setAccessLevel(selectedValue);
	};

	const checkChanges = () => {
		const initialDataCopy = extractCheckedPermissions(initialData);
		const updatedDataCopy = extractCheckedPermissions(allPermissions);
		return arraysEqual(initialDataCopy?.keys, updatedDataCopy?.keys);
	};

	const extractCheckedPermissions = (data: Group[]) => {
		const checkedPermissions: Group[] = [];

		data?.forEach((group: any) => {
			group?.list?.forEach((item: any) => {
				item?.permissions?.forEach((permission: any) => {
					if (permission?.checked === true) {
						checkedPermissions.push(permission?.key);
					}
				});
			});
		});

		return { keys: checkedPermissions };
	};

	const updateUserPermissions = () => {
		setDisable(true);
		const updatedPermissions = extractCheckedPermissions(allPermissions);
		apiService?.setUserPermissions(
			updatedPermissions,
			(response: AxiosResponse) => {
				setAlert({
					message: 'User Permissions Updated Successfully',
					severity: 'success',
				});
				setDisable(false);
				setApiCall(true);
			},
			(error: AxiosError) => {
				displayCustomError('Something went wrong!');
				setDisable(false);
			},
			user?.telephone
		);
	};

	const getPermissions = () => {
		apiService?.getAllPermissions(
			(response: AxiosResponse) => {
				//next call
				getUserPermissions(response?.data?.data);
			},
			(error: AxiosError) => {
				setLoadingData(false);
				displayCustomError('Something went wrong!');
			},
			user?.telephone
		);
	};

	const getUserPermissions = (allPermission: Group[]) => {
		apiService?.getUserPermissions(
			(response: AxiosResponse) => {
				const keys = response?.data?.data;
				const permissions = allPermission.map((group) => {
					return {
						group: group?.group,
						list: group?.list?.map((item) => {
							return {
								category: item?.category,
								permissions: item?.permissions?.map((permission) => {
									if (keys.includes(permission?.key)) {
										return { ...permission, checked: true };
									}
									return { ...permission, checked: false };
								}),
							};
						}),
					};
				});
				// Deep copy of permissions array
				const allPermissions = structuredClone(permissions);
				const initialData = structuredClone(permissions);

				setAllPermissions(allPermissions);
				setInitialData(initialData);
				setLoadingData(false);
				if (isApiCall) {
					setApiCall(false);
				}
			},
			(error: AxiosError) => {
				setLoadingData(false);
				displayCustomError('Something went wrong!');
			},
			user?.telephone
		);
	};

	useEffect(() => {
		getPermissions();
	}, [user?.telephone, isApiCall]);

	useEffect(() => {
		return () => {
			setView('team');
		};
	}, []);

	if (loadingData) {
		return (
			<Box
				sx={{
					width: '100%',
					height: '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<div className="chat__body__loadingMore__wrapper">
					<CircularProgress size={28} />
				</div>
			</Box>
		);
	}

	return (
		<StyledContainer>
			<div className="container1">
				<div id="profile">
					<CustomAvatar src={user?.image} style={{ width: '56px', height: '56px' }} />
					<div id="profile-info">
						<h3>{user?.firstname}</h3>
						<p>{user?.email}</p>
					</div>
				</div>
				<div id="access-user">
					<p>Access Level:</p>
					<span>{user?.type}</span>
					{/* <LabeledSelectField
						options={[
							{ value: 'user', id: '1' },
							{ value: 'admin', id: '2' },
						]}
						value={accessLevel}
						handleChange={handleChange}
						styles={{ width: '249px', height: '36px' }}
					/> */}
				</div>
			</div>
			<Divider style={{ width: '100%', height: '1.5px' }} />
			<div className="buttonContainer">
				<h3 id="heading">Permissions</h3>
				<CustomButton
					title={
						disable ? (
							<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
						) : (
							'Update Permissions'
						)
					}
					color="primary"
					styles={{ height: '36px', width: '190px' }}
					handleClick={() => updateUserPermissions()}
					disabled={disable || checkChanges()}
				/>
			</div>
			<PermissionContainer
				accessLevel={accessLevel}
				data={allPermissions}
				setData={setAllPermissions}
			/>
		</StyledContainer>
	);
};

export default Permission;
